/**
 * SEMU Header Controller - Modern 2025 Navigation System
 * Professional UI/UX with consistent interactions
 */

class SEMUHeaderController {
  constructor() {
    this.header = document.querySelector('.header');
    this.navigation = document.querySelector('.header__navigation');
    this.navItems = document.querySelectorAll('.header__nav-item');
    this.megaMenus = document.querySelectorAll('.meganav');
    this.dropdowns = document.querySelectorAll('.header__dropdown');
    this.backdrop = document.querySelector('.header__menu-backdrop');
    this.mobileMenuBtn = document.querySelector('.header__mobile-menu');

    this.activeMenu = null;
    this.hoverTimeout = null;
    this.isTouch = 'ontouchstart' in window;
    this.breakpoint = 990; // Changed to match CSS breakpoint

    this.init();
  }

  init() {
    if (!this.header) return;

    this.setupNavigation();
    this.setupMobileMenu();
    this.setupAccessibility();
    this.setupEventListeners();

    // Setup sub-dropdowns after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.setupSubDropdowns();
    }, 50); // Reduced from 100ms to 50ms

    // Add touch class for touch devices
    if (this.isTouch) {
      document.documentElement.classList.add('touch-device');
    }
  }

  setupNavigation() {
    this.navItems.forEach((item, index) => {
      const link = item.querySelector('.header__nav-link');

      // Find mega menu by data-mega-menu-trigger attribute
      const megaMenuTrigger = link?.getAttribute('data-mega-menu-trigger');
      const megaMenu = megaMenuTrigger ? document.getElementById(`mega-menu-${megaMenuTrigger}`) : null;
      const dropdown = item.querySelector('.header__dropdown');
      const menu = megaMenu || dropdown;

      if (!link || !menu) return;

      // Setup ARIA attributes
      const menuId = menu.id || `menu-${index}`;
      menu.id = menuId;
      link.setAttribute('aria-controls', menuId);

      // Desktop hover interactions
      if (!this.isTouch) {
        // Link hover for precise control
        link.addEventListener('mouseenter', () => {
          if (window.innerWidth >= this.breakpoint) {
            clearTimeout(this.hoverTimeout);
            this.showMenu(item, menu);
          }
        });

        // Item mouseleave for dropdown transition
        item.addEventListener('mouseleave', () => {
          if (window.innerWidth >= this.breakpoint) {
            this.hoverTimeout = setTimeout(() => {
              this.hideMenu(menu);
            }, 50); // Reduced from 150ms to 50ms for faster response
          }
        });

        // Mega menu hover (critical for keeping menu open)
        if (megaMenu) {
          megaMenu.addEventListener('mouseenter', () => {
            if (window.innerWidth >= this.breakpoint) {
              clearTimeout(this.hoverTimeout);
            }
          });

          megaMenu.addEventListener('mouseleave', () => {
            if (window.innerWidth >= this.breakpoint) {
              this.hoverTimeout = setTimeout(() => {
                this.hideMenu(menu);
              }, 80); // Reduced from 200ms to 80ms for faster response
            }
          });

          // Also add hover protection for mega menu links
          const megaMenuLinks = megaMenu.querySelectorAll('a');
          megaMenuLinks.forEach((menuLink) => {
            menuLink.addEventListener('mouseenter', () => {
              clearTimeout(this.hoverTimeout);
            });
          });
        }

        // Dropdown hover (same logic as mega menu for consistency)
        if (dropdown) {
          dropdown.addEventListener('mouseenter', () => {
            if (window.innerWidth >= this.breakpoint) {
              clearTimeout(this.hoverTimeout);
            }
          });

          dropdown.addEventListener('mouseleave', () => {
            if (window.innerWidth >= this.breakpoint) {
              this.hoverTimeout = setTimeout(() => {
                this.hideMenu(menu);
              }, 50); // Reduced from 150ms to 50ms for faster response
            }
          });

          // Also add hover protection for dropdown links
          const dropdownLinks = dropdown.querySelectorAll('a');
          dropdownLinks.forEach((menuLink) => {
            menuLink.addEventListener('mouseenter', () => {
              clearTimeout(this.hoverTimeout);
            });
          });
        }
      }

      // Click interactions for touch devices and mobile
      link.addEventListener('click', (e) => {
        if (this.isTouch || window.innerWidth < this.breakpoint) {
          e.preventDefault();
          this.toggleMenu(item, menu);
        }
      });

      // Keyboard navigation
      link.addEventListener('keydown', (e) => {
        this.handleKeyNavigation(e, item, menu);
      });

      // Setup menu item keyboard navigation
      this.setupMenuKeyNavigation(menu);
    });
  }

  setupSubDropdowns() {
    // Setup sub-dropdown functionality for multi-level menus
    const dropdownItems = document.querySelectorAll('.header__dropdown-item--has-children');

    dropdownItems.forEach((item) => {
      const link = item.querySelector('.header__dropdown-link');
      const subDropdown = item.querySelector('.header__dropdown--sub');

      if (!link || !subDropdown) {
        console.warn('⚠️ Missing link or sub-dropdown for item:', item);
        return;
      }

      let subHoverTimeout = null;

      // Desktop hover behavior
      if (!this.isTouch) {

        item.addEventListener('mouseenter', (e) => {
          if (window.innerWidth >= this.breakpoint) {
            clearTimeout(subHoverTimeout);
            subDropdown.classList.add('active');
            subDropdown.setAttribute('aria-hidden', 'false');
            link.setAttribute('aria-expanded', 'true');
          }
        });

        item.addEventListener('mouseleave', () => {
          if (window.innerWidth >= this.breakpoint) {
            subHoverTimeout = setTimeout(() => {
              subDropdown.classList.remove('active');
              subDropdown.setAttribute('aria-hidden', 'true');
              link.setAttribute('aria-expanded', 'false');
            }, 30); // Reduced from 150ms to 30ms for instant response
          }
        });

        // Keep menu open when hovering on sub-dropdown
        subDropdown.addEventListener('mouseenter', () => {
          clearTimeout(subHoverTimeout);
        });

        subDropdown.addEventListener('mouseleave', () => {
          subHoverTimeout = setTimeout(() => {
            subDropdown.classList.remove('active');
            subDropdown.setAttribute('aria-hidden', 'true');
            link.setAttribute('aria-expanded', 'false');
          }, 30); // Reduced from 150ms to 30ms for instant response
        });
      }

      // Click behavior for mobile and touch devices
      link.addEventListener('click', (e) => {
        if (this.isTouch || window.innerWidth < this.breakpoint) {
          if (link.getAttribute('aria-expanded') === 'true') {
            // If submenu is open, allow link navigation
            return;
          }
          e.preventDefault();
          const isActive = subDropdown.classList.contains('active');

          // Close other sub-dropdowns
          document.querySelectorAll('.header__dropdown--sub.active').forEach((otherSub) => {
            if (otherSub !== subDropdown) {
              otherSub.classList.remove('active');
              otherSub.setAttribute('aria-hidden', 'true');
              otherSub.parentElement.querySelector('.header__dropdown-link').setAttribute('aria-expanded', 'false');
            }
          });

          // Toggle current sub-dropdown
          subDropdown.classList.toggle('active', !isActive);
          subDropdown.setAttribute('aria-hidden', isActive ? 'true' : 'false');
          link.setAttribute('aria-expanded', isActive ? 'false' : 'true');
        }
      });

      // Keyboard navigation
      link.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowRight':
          case 'Enter':
          case ' ':
            e.preventDefault();
            subDropdown.classList.add('active');
            subDropdown.setAttribute('aria-hidden', 'false');
            link.setAttribute('aria-expanded', 'true');

            // Focus first link in sub-dropdown
            const firstSubLink = subDropdown.querySelector('.header__dropdown-link');
            if (firstSubLink) {
              firstSubLink.focus();
            }
            break;
          case 'Escape':
            subDropdown.classList.remove('active');
            subDropdown.setAttribute('aria-hidden', 'true');
            link.setAttribute('aria-expanded', 'false');
            link.focus();
            break;
        }
      });
    });
  }

  setupMobileMenu() {
    if (!this.mobileMenuBtn) return;
  }

  setupAccessibility() {
    // Set initial ARIA states
    this.megaMenus.forEach((menu) => {
      menu.setAttribute('role', 'menu');
      menu.setAttribute('aria-hidden', 'true');
    });

    this.dropdowns.forEach((dropdown) => {
      dropdown.setAttribute('role', 'menu');
      dropdown.setAttribute('aria-hidden', 'true');
    });
  }

  setupEventListeners() {
    // Close menus on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.header__navigation')) {
        this.hideAllMenus();
      }
    });

    // Close menus on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideAllMenus();
        this.focusActiveNavLink();
      }
    });

    window.addEventListener(
      'resize',
      this.debounce(() => {
        if (window.innerWidth < this.breakpoint) {
          this.hideAllMenus();
        }
      }, 150),
      { passive: true }
    );

    // Handle backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        this.hideAllMenus();
      });
    }
  }

  setupMenuKeyNavigation(menu) {
    const menuItems = menu.querySelectorAll('a');

    menuItems.forEach((item, index) => {
      item.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            const nextIndex = index < menuItems.length - 1 ? index + 1 : 0;
            menuItems[nextIndex].focus();
            break;

          case 'ArrowUp':
            e.preventDefault();
            const prevIndex = index > 0 ? index - 1 : menuItems.length - 1;
            menuItems[prevIndex].focus();
            break;

          case 'Home':
            e.preventDefault();
            menuItems[0].focus();
            break;

          case 'End':
            e.preventDefault();
            menuItems[menuItems.length - 1].focus();
            break;

          case 'Tab':
            // Allow normal tab behavior but close menu after a delay
            setTimeout(() => {
              if (!menu.contains(document.activeElement)) {
                this.hideMenu(menu);
              }
            }, 0);
            break;
        }
      });
    });
  }

  handleKeyNavigation(e, navItem, menu) {
    const link = navItem.querySelector('.header__nav-link');

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (menu) {
          e.preventDefault();
          this.toggleMenu(navItem, menu);
        }
        break;

      case 'ArrowDown':
        if (menu) {
          e.preventDefault();
          this.showMenu(navItem, menu);
          this.focusFirstMenuItem(menu);
        }
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.focusPreviousNavItem(navItem);
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.focusNextNavItem(navItem);
        break;
    }
  }

  showMenu(navItem, menu) {
    // Hide other menus first
    this.hideAllMenus();

    const link = navItem.querySelector('.header__nav-link');

    // Show current menu
    menu.classList.add('active');
    menu.setAttribute('aria-hidden', 'false');
    link.setAttribute('aria-expanded', 'true');

    if (menu.classList.contains('header__dropdown')) {
      requestAnimationFrame(() => {
        if (!menu.isConnected || !navItem.isConnected) return;
        const header = document.querySelector('.header-wrapper');
        if (!header) return;
        const headerBottom = header.getBoundingClientRect().bottom;
        const navItemTop = navItem.getBoundingClientRect().top;
        menu.style.top = `${headerBottom - navItemTop}px`;
      });

      setTimeout(() => {
        this.setupSubDropdownsForMenu(menu);
      }, 20);
    }

    // Show backdrop for mobile
    if (this.backdrop && window.innerWidth < this.breakpoint) {
      this.backdrop.classList.add('header__menu-backdrop--active');
      this.backdrop.setAttribute('aria-hidden', 'false');
    }

    this.activeMenu = menu;
  }

  setupSubDropdownsForMenu(menu) {
    const dropdownItems = menu.querySelectorAll('.header__dropdown-item--has-children');

    if (dropdownItems.length > 0) {
      // Re-run setup for these specific items
      this.setupSubDropdowns();
    }
  }

  hideMenu(menu) {
    if (!menu) return;

    // Simple class toggle
    menu.classList.remove('active');
    menu.setAttribute('aria-hidden', 'true');

    // Find trigger
    let trigger;
    if (menu.classList.contains('header__dropdown')) {
      const navItem = menu.closest('.header__nav-item');
      trigger = navItem?.querySelector('.header__nav-link');
      // Reset dropdown position
      menu.style.top = '';
    } else {
      const menuHandle = menu.getAttribute('data-menu-handle');
      trigger = document.querySelector(`[data-mega-menu-trigger="${menuHandle}"]`);
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (this.activeMenu === menu) {
      this.activeMenu = null;

      // Hide backdrop
      if (this.backdrop) {
        this.backdrop.classList.remove('header__menu-backdrop--active');
        this.backdrop.setAttribute('aria-hidden', 'true');
      }
    }
  }

  hideAllMenus() {
    [...this.megaMenus, ...this.dropdowns].forEach((menu) => {
      this.hideMenu(menu);
    });

    clearTimeout(this.hoverTimeout);
  }

  toggleMenu(navItem, menu) {
    if (menu.getAttribute('aria-hidden') === 'false') {
      this.hideMenu(menu);
    } else {
      this.showMenu(navItem, menu);
    }
  }

  focusFirstMenuItem(menu) {
    const firstLink = menu.querySelector('a');
    if (firstLink) {
      firstLink.focus();
    }
  }

  focusActiveNavLink() {
    const activeMenu = this.activeMenu;
    if (activeMenu) {
      const navItem = activeMenu.closest('.header__nav-item');
      const link = navItem?.querySelector('.header__nav-link');
      if (link) {
        link.focus();
      }
    }
  }

  focusPreviousNavItem(currentNavItem) {
    const navItems = [...this.navItems];
    const currentIndex = navItems.indexOf(currentNavItem);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : navItems.length - 1;
    const prevLink = navItems[prevIndex]?.querySelector('.header__nav-link');
    if (prevLink) {
      prevLink.focus();
    }
  }

  focusNextNavItem(currentNavItem) {
    const navItems = [...this.navItems];
    const currentIndex = navItems.indexOf(currentNavItem);
    const nextIndex = currentIndex < navItems.length - 1 ? currentIndex + 1 : 0;
    const nextLink = navItems[nextIndex]?.querySelector('.header__nav-link');
    if (nextLink) {
      nextLink.focus();
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Cart Icon Update Functionality
class SEMUCartController {
  constructor() {
    this.cartIcon = document.querySelector('[data-cart-icon]');
    this.cartCounter = document.querySelector('.header__cart-counter');

    this.init();
  }

  init() {
    if (!this.cartIcon) return;

    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      this.updateCartIcon(e.detail);
    });
  }

  updateCartIcon(cartData) {
    if (!cartData) return;

    const itemCount = cartData.item_count || 0;

    // Update counter
    if (this.cartCounter) {
      if (itemCount > 0) {
        this.cartCounter.textContent = itemCount < 100 ? itemCount : '99+';
        this.cartCounter.style.display = 'flex';
      } else {
        this.cartCounter.style.display = 'none';
      }
    }

    // Update aria-label
    this.cartIcon.setAttribute('aria-label', `Cart (${itemCount} ${itemCount === 1 ? 'item' : 'items'})`);
  }
}

// Sticky Header Functionality
class SEMUStickyHeader {
  constructor() {
    this.header = document.querySelector('.header');
    this.headerWrapper = document.querySelector('.header-wrapper');
    this.lastScrollY = window.scrollY;
    this.isSticky = false;

    this.init();
  }

  init() {
    if (!this.header) return;

    window.addEventListener('scroll', this.debounce(this.handleScroll.bind(this), 10));
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const headerHeight = this.header.offsetHeight;

    if (currentScrollY > headerHeight && !this.isSticky) {
      this.makeSticky();
    } else if (currentScrollY <= headerHeight && this.isSticky) {
      this.removeSticky();
    }

    this.lastScrollY = currentScrollY;
  }

  makeSticky() {
    this.isSticky = true;
    // Only add if not already present
    if (!this.headerWrapper.classList.contains('header--sticky')) {
      this.headerWrapper.classList.add('header--sticky');
    }

    // Set CSS custom property for header height
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  removeSticky() {
    this.isSticky = false;
    this.headerWrapper.classList.remove('header--sticky');
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new SEMUHeaderController();
  new SEMUCartController();
  new SEMUStickyHeader();
});
// Export for potential external use
window.SEMUHeader = {
  HeaderController: SEMUHeaderController,
  CartController: SEMUCartController,
  StickyHeader: SEMUStickyHeader,
};
